// Bootstrap the multi-server registry on app start.
//
// Why this exists (post-S11b catch-22): the frontend is served from
// Cloudflare Pages on `ide.asoasis.com` after the DNS flip. CF Pages does
// not run the API. So when a brand-new device opens the app for the
// first time and `useServersStore` is empty, `getActiveBaseUrl()` returns
// `''` and the first API call (e.g. `/api/auth/me`) goes to the same
// origin — which is CF Pages — and 404s. Login screen renders "Could
// not reach the server" and the user is locked out unless they manually
// open `https://vps1.asoasis.com` in another tab and add it via Settings.
//
// Fix: a tiny public manifest (`/asoasis-config.json`, baked into the
// CF Pages build) declares the canonical default server(s). On startup
// we fetch it once and pre-seed the store if no entries are persisted
// yet. Subsequent loads are no-ops because Zustand persist already has
// the servers in localStorage.
//
// Sprint UI compaction (2026-05-11) addendum — auto-prune orphan
// entries. Pre-D-013 the `ServerSelector` let the user add multiple
// per-VPS dashboards (vps1.asoasis.com, vps2.asoasis.com, etc.) to
// localStorage. Post-D-013 only the control plane (vps1.asoasis.com)
// has a dashboard backend; every other VPS is a runner-only host with
// no `/api/health` endpoint. The visible selector has been removed
// (W12 in `docs/edge-cases.md`) but historical localStorage entries
// can still leave the store carrying ghost "offline" servers that
// `getActiveBaseUrl()` could in theory still select on. This bootstrap
// step now reconciles localStorage against the manifest: any server
// in the registry whose `id` is NOT in `defaultServers[]` is dropped.
// Trade-off: removes Santi's ability to add a custom dev backend via
// localStorage hand-edit, which has zero current use case.
//
// Same-origin fetch only — no CORS or auth concerns. The manifest is
// public by design (hostnames, not credentials).

import { useServersStore, type ServerEntry } from '../stores/servers.store';

interface AsOasisConfig {
  defaultServers?: Array<Pick<ServerEntry, 'id' | 'name' | 'baseUrl'>>;
}

// 2 s budget — manifest is a tiny static file from the same origin (or
// from Express in single-server mode). If it doesn't return in 2 s the
// network is broken anyway and a longer wait would just delay the
// "could not reach" error the user is going to see regardless.
const FETCH_TIMEOUT_MS = 2_000;

export async function bootstrapDefaultServers(): Promise<void> {
  let res: Response;
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
    res = await fetch('/asoasis-config.json', {
      cache: 'no-cache',
      signal: ctrl.signal,
    });
    clearTimeout(timer);
  } catch {
    // Manifest unreachable. We still need to prune orphans relative to
    // whatever the hard-coded canonical id is. Without a manifest we
    // cannot prune safely, so we bail out: stale entries survive until
    // the next successful boot. This is acceptable because the
    // post-D-013 daily flow (CF Pages → vps1 API) requires the
    // manifest to load anyway; if it didn't load, bigger things are
    // broken.
    return reconcileWithSeeds(null);
  }

  if (!res.ok) return reconcileWithSeeds(null);

  let config: AsOasisConfig;
  try {
    config = (await res.json()) as AsOasisConfig;
  } catch {
    return reconcileWithSeeds(null);
  }

  const seeds = (config.defaultServers ?? []).filter(
    (s) => s && typeof s.id === 'string' && typeof s.baseUrl === 'string',
  );

  reconcileWithSeeds(seeds);
}

function reconcileWithSeeds(
  seeds: Array<Pick<ServerEntry, 'id' | 'name' | 'baseUrl'>> | null,
): void {
  const initial = useServersStore.getState();
  const allowedIds = seeds === null ? null : new Set(seeds.map((s) => s.id));

  // Step 1 — drop orphans. Only the manifest's `id`s are allowed in
  // the registry. Skipped when the manifest failed to load (allowedIds
  // === null) to avoid prune-on-network-flake.
  let nextServers = initial.servers;
  if (allowedIds !== null) {
    nextServers = nextServers.filter((s) => allowedIds.has(s.id));
  }

  // Step 2 — pre-seed missing canonical entries. If the manifest
  // declares vps1 but localStorage doesn't have it yet (fresh device),
  // copy the canonical row in.
  if (seeds !== null) {
    for (const seed of seeds) {
      if (!nextServers.some((s) => s.id === seed.id)) {
        nextServers = [...nextServers, { ...seed, status: 'unknown' }];
      }
    }
  }

  // Step 3 — guarantee `activeServerId` points at a present entry.
  // If the previous active was just pruned, fall back to the first
  // manifest seed.
  const activeStillPresent = nextServers.some((s) => s.id === initial.activeServerId);
  const nextActive = activeStillPresent
    ? initial.activeServerId
    : (nextServers[0]?.id ?? null);

  // Bail-out: nothing actually changed. Avoids a no-op `setState` that
  // would still trigger every subscriber.
  if (
    nextServers.length === initial.servers.length &&
    nextActive === initial.activeServerId &&
    nextServers.every((s, i) => s.id === initial.servers[i]?.id)
  ) {
    return;
  }

  useServersStore.setState({
    servers: nextServers,
    activeServerId: nextActive,
  });
}
